import { useFlickity } from '@/components/hooks/useFlickity'
import { BaseResults } from 'libs/athos/components/recommendations/BaseResults'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useState, useEffect } from 'preact/hooks'
import clsx from 'clsx'

export function CarouselResults({
  className,
  wrapperClassName = '',
  options = { pageDots: true, cellAlign: 'left' },
  productCardSettings,
  CardComponent,
  SkeletonComponent,
  beaconEnabled = true,
  'data-dl-section-id': dataDlSectionId
}) {
  const { results, isLoading } = useAthosContext()
  const [skeletonNode, setSkeletonNode] = useState()
  const [realNode, setRealNode] = useState()
  const [containerNode, setContainerNode] = useState()
  const [showRealCarousel, setShowRealCarousel] = useState(false)

  useFlickity(skeletonNode, options, [])
  const { SliderPortal, flickity, inViewRange } = useFlickity(realNode, options, [results])

  const hasData = !isLoading && results?.results

  useEffect(() => {
    if (!hasData) {
      setShowRealCarousel(false)
      return
    }

    if (!inViewRange) {
      setShowRealCarousel(true)
      return
    }

    if (flickity && containerNode) {
      const skeletonHeight = containerNode.getBoundingClientRect().height
      containerNode.style.height = `${skeletonHeight}px`

      flickity.resize()
      flickity.reloadCells()

      const timer = setTimeout(() => {
        containerNode.style.height = ''
        setShowRealCarousel(true)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [hasData, flickity, containerNode, inViewRange])

  return (
    <BaseResults
      className={className}
      wrapperClassName={wrapperClassName}
      productCardSettings={productCardSettings}
      beaconEnabled={beaconEnabled}
      {...(CardComponent && { CardComponent })}
      {...(SkeletonComponent && { SkeletonComponent })}
      renderWrapper={(skeletonItems, realItems) => (
        <div ref={setContainerNode} className="relative grid grid-cols-1 grid-rows-1 w-full h-fit transition-all">
          <div className={clsx(
            'w-full h-fit row-start-1 col-start-1 transition-opacity',
            !showRealCarousel ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}>
            <div
              ref={setSkeletonNode}
              className={clsx(
                'flex w-full h-fit',
                options.pageDots && 'pb-1.5 lg:pb-1.625'
              )}
            >
              {skeletonItems}
            </div>
          </div>

          <div className={clsx(
            'w-full h-fit row-start-1 col-start-1 transition-opacity',
            showRealCarousel ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}>
            <div
              ref={setRealNode}
              className={clsx(
                'flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625'
              )}
              data-dl-section-id={dataDlSectionId}
            >
              <SliderPortal>
                {realItems}
              </SliderPortal>
            </div>
          </div>
        </div>
      )}
    />
  )
}
